/// <reference types="dhtmlxscheduler" />
import Dialog from "@material-ui/core/Dialog"
import IconButton from "@material-ui/core/IconButton"
import React from "react"
import CalendarForm from "./CalendarForm"
import TaskCardTitle from "../../tasks/components/TaskCardTitle"
import TodoCardTitle from "../../todos/components/TodoCardTitle"
import CalendarEventCardTitle from "./CalendarEventCardTitle"
import TaskDetails from "../../tasks/components/TaskDetails"
import TodoDetails from "../../todos/components/TodoDetails"
import CalendarEventDetails from "./CalendarEventDetails"
import FullPath from "../../components/FullPath"
import Button from "@material-ui/core/Button"
import DialogActions from "@material-ui/core/DialogActions"
import DialogContent from "@material-ui/core/DialogContent"
import Typography from "@material-ui/core/Typography"
import { makeStyles, Theme } from "@material-ui/core"
import { X } from "react-feather"
import { useLocation } from "react-router-dom"
import {
  CalendarEventData,
  CalendarEventUpdates,
  CalendarTaskData,
  CalendarTaskUpdates,
  CalendarTodoData,
  CalendarTodoUpdates,
  EventType,
  RecurringEventType,
} from "../utils"
import { DateTimeService } from "../../services/date-time-service"
import { useAuthUser } from "../../users/hooks/use-auth-user"
import { TaskViewModel } from "../../tasks/api/task"
import { CalendarEventViewModel } from "../api/calendar-event"
import { TodoViewModel } from "../../todos/api/todo"
import { useDateTranslations } from "../../hooks/use-date-translations"
import { useIsSmallScreen } from "../../hooks/use-is-small-screen"
import {
  createDateScaleTemplate,
  createEventBarTemplate,
  createEventTextTemplate,
  createMonthDayTemplate,
  createTooltipTemplate,
  createWeekDateClassTemplate,
  createWeekHeaderTemplate,
} from "../scheduler-templates"
import { useTodoMutations } from "../../todos/hooks/use-todo-mutations"
import { useTranslation } from "react-i18next"
import { calendarEventApi } from "../api"
import { useAppDispatch } from "../../store"
import { CalendarSettingsViewModel } from "../../calendar-settings/api/calendar-settings"

const SCHEDULER_HEADER = {
  full: ["day", "week", "month", "minicalendar", "date", "prev", "today", "next"],
  compact: {
    rows: [{ cols: ["date"] }, { cols: ["day", "week", "minicalendar", "spacer", "prev", "today", "next"] }],
  },
}

const useEditRecurringEvent = () => {
  const [currentRecurringEvent, setCurrentRecurringEvent] = React.useState<RecurringEventType | null>(null)
  const [openEditRecurringEventConfirmation, setOpenEditRecurringEventConfirmation] = React.useState(false)

  const handleClickRecurringEvent = (event: RecurringEventType) => {
    setCurrentRecurringEvent(event)
    setOpenEditRecurringEventConfirmation(true)
  }

  const clearState = () => {
    setCurrentRecurringEvent(null)
    setOpenEditRecurringEventConfirmation(false)
  }

  return {
    openEditRecurringEventConfirmation,
    currentRecurringEvent,
    handleClickRecurringEvent,
    setOpenEditRecurringEventConfirmation,
    clearState,
  }
}

const Calendar = ({
  id = "__scheduler__v2",
  events,
  recurringEvents,
  entitiesById,
  canAddCalendarEvents,
  canAddTasks,
  canAddTodos,
  onAddCalendarEvent,
  onAddTask,
  onAddTodo,
  onUpdateCalendarEvent,
  onUpdateTask,
  onUpdateTodo,
  calendarSettings,
  showCaption = false,
}: CalendarProps) => {
  const { user } = useAuthUser()
  const { updateStatus } = useTodoMutations()
  const dispatch = useAppDispatch()
  const dateFormat = calendarSettings.dateFnsDateFormat

  const dateTimeService = React.useMemo(() => {
    return new DateTimeService({
      dateFormat,
      timeZone: user?.timeZone,
    })
  }, [dateFormat, user?.timeZone])

  const timeZonedNormalEvents = React.useMemo(() => {
    return events.map((event: EventType) => {
      const { start_date, end_date } = event
      const zonedStartDate = dateTimeService.removeTimezoneOffset(start_date)
      const zonedEndDate = dateTimeService.removeTimezoneOffset(end_date)
      return { ...event, start_date: zonedStartDate, end_date: zonedEndDate }
    })
  }, [dateTimeService, events])
  const timeZonedRecurringEvents = React.useMemo(() => {
    return recurringEvents.map((event: RecurringEventType) => {
      const { start_date, end_date } = event
      const zonedStartDate = dateTimeService.removeTimezoneOffset(start_date)
      const zonedEndDate = end_date
        ? dateTimeService.removeTimezoneOffset(end_date)
        : dateTimeService.getLargestPossibleDate()

      let zonedEventLength = event.event_length
      if (event.event_pid) {
        const eventLenthDate = new Date(zonedEventLength * 1000)
        const zonedEventLengthDate = dateTimeService.removeTimezoneOffset(eventLenthDate)
        zonedEventLength = zonedEventLengthDate.getTime() / 1000
      }

      return { ...event, start_date: zonedStartDate, end_date: zonedEndDate, event_length: zonedEventLength }
    })
  }, [dateTimeService, recurringEvents])
  const timeZonedEvents = React.useMemo(() => {
    return [...timeZonedNormalEvents, ...timeZonedRecurringEvents]
  }, [timeZonedNormalEvents, timeZonedRecurringEvents])

  const { currentRecurringEvent, handleClickRecurringEvent, openEditRecurringEventConfirmation, clearState } =
    useEditRecurringEvent()

  const classes = useStyles()
  const translations = useTranslations()
  const { t: calendarEventTranslations } = useTranslation("calendarEvent")
  const { t: taskTranslations } = useTranslation("task")
  const { t: projectTranslations } = useTranslation("project")
  const { t } = useTranslation("common")
  const location = useLocation()
  const isSmallScreen = useIsSmallScreen()
  const calendarInstanceRef = React.useRef<SchedulerStatic | null>(null)
  const lightBoxRootElRef = React.useRef<HTMLDivElement | null>(null)
  const eventFormTitleInputRef = React.useRef<HTMLInputElement | null>(null)
  const [startDate, setStartDate] = React.useState<string | null>(null)
  const [endDate, setEndDate] = React.useState<string | null>(null)
  const [currentEventId, setCurrentEventId] = React.useState<string | null>(null)
  const [openNewOccurance, setOpenNewOccurance] = React.useState<Boolean>(false)
  const [newlyCreatedEventId, setNewlyCreatedEventId] = React.useState<string | null>(null)

  const currentEvent = timeZonedEvents.find((evt) => evt.id === currentEventId)
  const newEvent = timeZonedEvents.find((evt) => evt.id === newlyCreatedEventId)
  const onBeforeEventChanged = (event: EventType, _: any, isNew: boolean) => {
    const eventEndDate = dateTimeService.addTimezoneOffset(event.end_date).toISOString()
    const eventStartDate = dateTimeService.addTimezoneOffset(event.start_date).toISOString()
    if (isNew) {
      // sync startDate and endDate with dates of new event
      setStartDate(eventStartDate)
      setEndDate(eventEndDate)
      return true // returning falsy from here will cancel this event and cause undesired behaviour
    } else if (event.type === "task") {
      return onUpdateTask(event.id, {
        plannedEndDate: eventEndDate,
        plannedStartDate: eventStartDate,
      })
    } else if (event.type === "todo") {
      const dueDate = eventEndDate
      let enableTimeComponent = false
      if (dueDate && !dateTimeService.hasMidnightTime(dueDate)) enableTimeComponent = true
      return onUpdateTodo(event.id, { dueDate, enableTimeComponent })
    } else if (event.type === "calendarEvent") {
      const [parentEventId, eventLength] = event.id.split("#")
      const shouldCreateNewOccurance = Boolean(eventLength)
      if (shouldCreateNewOccurance) {
        const eventLengthDate = new Date(parseInt(eventLength) * 1000)
        const timeZonedEventLengthDate = dateTimeService.addTimezoneOffset(eventLengthDate)

        return onAddCalendarEvent({
          title: event.text,
          startDate: eventStartDate,
          endDate: eventEndDate,
          description: event.details || "",
          parentEventId,
          eventLength: timeZonedEventLengthDate.getTime(),
        })
      }

      return onUpdateCalendarEvent(event.id, {
        startDate: eventStartDate,
        endDate: eventEndDate,
      })
    }
    throw new Error("Unexpected error")
  }

  const onEventAdded = (id: string) => {
    calendarInstanceRef.current?.deleteEvent(id)
  }

  const showLightBox = (id: string) => {
    if (canAddTasks || canAddTodos || canAddCalendarEvents) {
      if (calendarInstanceRef.current && lightBoxRootElRef.current) {
        calendarInstanceRef.current.startLightbox(id, lightBoxRootElRef.current)
        eventFormTitleInputRef.current?.focus()
      }
    }
  }

  const closeForm = (save: boolean) => {
    calendarInstanceRef.current?.endLightbox(save, lightBoxRootElRef.current as HTMLElement)
    if (!save) {
      dispatch(calendarEventApi.util.invalidateTags(["CalendarEvent", "Todo"]))
    }
    // reset state of start and end date
    setStartDate(null)
    setEndDate(null)
    setCurrentEventId(null)
    setNewlyCreatedEventId(null)
  }

  const handleBeforeEventChange = React.useCallback(onBeforeEventChanged, [
    dateTimeService,
    onUpdateTask,
    onUpdateTodo,
    onUpdateCalendarEvent,
    onAddCalendarEvent,
  ])
  const handleShowLightBox = React.useCallback(showLightBox, [canAddTasks, canAddTodos, canAddCalendarEvents])
  const handleEventAdded = React.useCallback(onEventAdded, [])
  const handleClickEditOccurrence = () => {
    if (currentRecurringEvent) {
      const instanceExists = timeZonedRecurringEvents.find((event) => event.id === currentRecurringEvent.id)
      if (instanceExists) {
        setCurrentEventId(currentRecurringEvent.id)
        clearState()
        return
      }

      const parentEvent = timeZonedRecurringEvents.find((event) => event.id === currentRecurringEvent.event_pid)
      if (!parentEvent) return clearState()
      const endDate = new Date(
        currentRecurringEvent.start_date.getTime() + parentEvent.event_length * 1000
      ).toISOString()
      const recurringInstanceData: CalendarEventData = {
        title: currentRecurringEvent.text,
        startDate: currentRecurringEvent.start_date.toISOString(),
        endDate: endDate,
        description: parentEvent.details || "",
        parentEventId: currentRecurringEvent.event_pid,
        eventLength: currentRecurringEvent.start_date.getTime(),
      }
      onAddCalendarEvent(recurringInstanceData)
      setOpenNewOccurance(true)
    } else {
      clearState()
    }
  }

  React.useEffect(() => {
    const calendar = calendarInstanceRef.current
    if (!calendar) return
    const header = isSmallScreen ? SCHEDULER_HEADER.compact : SCHEDULER_HEADER.full
    // @ts-expect-error
    calendar.config.header = header
    calendar.xy.nav_height = isSmallScreen ? 80 : 60
    calendar.setCurrentView(new Date(), isSmallScreen ? "week" : "")

    const DateTemplate = createDateScaleTemplate({
      calendarSettings,
      translations,
      showMonthName: !isSmallScreen,
    })

    calendar.templates.week_scale_date = DateTemplate
    calendar.templates.day_scale_date = DateTemplate

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isSmallScreen])

  React.useLayoutEffect(() => {
    if (!calendarInstanceRef.current) {
      const schedulerConfig = {
        container: id,
        config: {
          readonly: !canAddTasks && !canAddTodos && !canAddCalendarEvents,
          details_on_create: true,
          details_on_dblclick: true,
          header: SCHEDULER_HEADER.full,
          scroll_hour: new Date().getHours(),
          mark_now: true,
          now_date: new Date(),
        },
        data: { timeZonedEvents },
        templates: {
          event_text: createEventTextTemplate(),
          event_bar_text: createEventBarTemplate(),
        },
      }
      // Initialize the calendar instance
      //@ts-ignore
      calendarInstanceRef.current = Scheduler.getSchedulerInstance(schedulerConfig)
      const calendar = calendarInstanceRef.current as SchedulerStatic
      /**
       * Calendar plugins
       */
      // @ts-ignore
      calendar.plugins({
        limit: true,
        minical: true,
        tooltip: true,
        recurring: true,
      })

      /** Calendar header week date customization
       * This is needed to show the week number in the header of the calendar when the week view is active
       */
      calendar.templates.week_date = createWeekHeaderTemplate({
        dateTimeService: dateTimeService,
        weekLabel: translations.weekLabel,
      })

      /** Calendar month day template customization
       * This is needed to show the week number in the month view and
       * also to apply custom styling to holidays and weekends
       */
      calendar.templates.month_day = createMonthDayTemplate({
        calendar,
        weekShortLabel: t("week.short"),
      })

      /** Calendar timeZonedEvents */
      calendar.attachEvent("onBeforeEventChanged", handleBeforeEventChange)
      calendar.attachEvent("onEventAdded", handleEventAdded)
      calendar.attachEvent("onClick", (id: string) => {
        const foundEvent = calendar.getEvent(id)
        const isRecurringEvent = foundEvent?.rec_type || foundEvent?.event_pid
        if (isRecurringEvent) return handleClickRecurringEvent(foundEvent)
        const [eventId] = id.split("#")
        setCurrentEventId(eventId)
        calendar.showLightbox(eventId)
      })
      calendar.attachEvent("onDblClick", (id: string) => {
        setCurrentEventId(id)
        calendar.showLightbox(id)
        calendar.showLightbox(id)
      })

      /** Calendar methods */
      calendar.showLightbox = handleShowLightBox

      /** Calendar template customization */
      calendar.templates.week_date_class = createWeekDateClassTemplate({ calendarSettings })

      calendar.templates.tooltip_text = createTooltipTemplate({
        dateTimeService: dateTimeService,
        taskTranslations,
        projectTranslations,
      })

      /**
       * Overrides Scheduler's default week_start logic to support a configurable
       * first day of the week (0–6 where 0 = Sunday, 1 = Monday ...).
       *
       * Example (firstDayOfWeek = 1, Monday):
       *   Sunday (0) → shift = 6 → returns previous Monday
       *   Tuesday (2) → shift = 1 → returns Monday
       */
      calendar.date.week_start = function (date) {
        const day = date.getDay() // 0..6 (Sun..Sat)
        const shift = (day - calendarSettings.firstDayOfWeek + 7) % 7
        return this.date_part(this.add(date, -shift, "day"))
      }
      calendarInstanceRef.current?.parse(timeZonedEvents)
    }
  }, [
    canAddTasks,
    canAddTodos,
    canAddCalendarEvents,
    timeZonedEvents,
    handleBeforeEventChange,
    handleShowLightBox,
    handleEventAdded,
    id,
    showCaption,
    dateTimeService,
    handleClickRecurringEvent,
    translations.abbreviatedDayNames,
    translations.fullMonthNames,
    translations.weekLabel,
    calendarSettings,
    isSmallScreen,
    taskTranslations,
    projectTranslations,
    t,
  ])
  React.useEffect(() => {
    return () => {
      // destroy the calendar instance
      if (calendarInstanceRef.current) {
        // @ts-ignore
        calendarInstanceRef.current.destructor()
        calendarInstanceRef.current = null
      }
    }
  }, [location.pathname])
  React.useEffect(() => {
    if (calendarInstanceRef.current) {
      const calendarTranslations = makeCalendarTranslations(translations)
      // @ts-ignore
      calendarInstanceRef.current.i18n.setLocale(calendarTranslations)
      // @ts-ignore
      calendarInstanceRef.current.render()
    }
  }, [translations])

  /**
   * This effect is responsible for updating the calendar
   * when any event is updated
   */
  React.useEffect(() => {
    if (calendarInstanceRef.current) {
      calendarInstanceRef.current.clearAll()
      calendarInstanceRef.current.parse(timeZonedEvents)
    }
  }, [timeZonedEvents])

  /**
   * This effect ends the light box
   * when an event gets deleted from calendar
   */
  React.useEffect(() => {
    if (currentEventId && !currentEvent) {
      setCurrentEventId(null)
      calendarInstanceRef.current?.endLightbox(false, lightBoxRootElRef.current as HTMLElement)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeZonedEvents, currentEventId])

  React.useEffect(() => {
    if (openNewOccurance) {
      if (!currentRecurringEvent) {
        setOpenNewOccurance(false)
        clearState()
        return
      }

      const foundOccurance = timeZonedRecurringEvents.find((event) => {
        const occuranceId = event.event_pid + "#" + event.event_length
        return occuranceId === currentRecurringEvent.id
      })

      if (foundOccurance) {
        setCurrentEventId(foundOccurance.id)
        setOpenNewOccurance(false)
        clearState()
      }
    }
  }, [clearState, currentRecurringEvent, openNewOccurance, timeZonedRecurringEvents])

  React.useEffect(() => {
    const calendar = calendarInstanceRef.current
    if (!calendar) return

    const renderCheckboxes = () => {
      const nodes = document.querySelectorAll(".todo-checkbox-root")
      nodes.forEach((node) => {
        const id = node.getAttribute("data-id")
        if (!id) return
        const event = calendar.getEvent(id)
        if (!event || event.type !== "todo") return
        const completed = Boolean(event.completed)
        // Native checkbox
        const input = document.createElement("input")
        input.type = "checkbox"
        input.checked = completed
        input.style.pointerEvents = "auto"
        input.title = completed
          ? calendarEventTranslations("calendarEventDetails.markAsIncomplete")
          : calendarEventTranslations("calendarEventDetails.markAsComplete")

        input.addEventListener("click", (e) => {
          e.stopPropagation()
          const target = e.target as HTMLInputElement
          updateStatus(id, { completed: target.checked })
        })

        // Clear previous children
        node.innerHTML = ""
        // Append input and styled span
        node.appendChild(input)
        const span = document.createElement("span")
        span.className = "custom-checkbox"
        if (input.checked) span.classList.add("checked")
        node.appendChild(span)
      })
    }

    setTimeout(renderCheckboxes, 0)

    const eventId = (calendar as any).attachEvent("onDataRender", renderCheckboxes)

    return () => {
      ;(calendar as any).detachEvent(eventId)
    }
  }, [calendarEventTranslations, updateStatus])

  const onCreateTodo = async (todo: CalendarTodoData) => {
    const dueDate = todo.dueDate
    let enableTimeComponent = false
    if (dueDate && !dateTimeService.hasMidnightTime(dueDate)) enableTimeComponent = true
    const newTodo = await onAddTodo({ ...todo, enableTimeComponent })
    if (newTodo) setNewlyCreatedEventId(newTodo.id)
  }
  const onCreateCalendarEvent = async (calendarEvent: CalendarEventData) => {
    const newCalendarEvent = await onAddCalendarEvent(calendarEvent)
    if (newCalendarEvent) setNewlyCreatedEventId(newCalendarEvent.id)
  }
  const onCreateTask = async (task: CalendarTaskData) => {
    const newTask = await onAddTask(task)
    if (newTask) setNewlyCreatedEventId(newTask.id)
  }

  function showMiniCalendar() {
    if (calendarInstanceRef.current?.isCalendarVisible()) {
      calendarInstanceRef.current?.destroyCalendar()
    } else {
      calendarInstanceRef.current?.renderCalendar({
        position: "dhx_minical_icon",
        date: calendarInstanceRef.current?.date,
        navigation: true,
        handler: function (date: Date) {
          calendarInstanceRef.current?.setCurrentView(date)
          calendarInstanceRef.current?.destroyCalendar()
        },
      })
    }
  }

  const shouldOpenCurrentEventDialog =
    currentEventId &&
    currentEvent &&
    (currentEvent.type === "task" || currentEvent.type === "todo" || currentEvent.type === "calendarEvent")
  const shouldOpenNewEventDialog =
    newlyCreatedEventId &&
    newEvent &&
    (newEvent.type === "task" || newEvent.type === "todo" || newEvent.type === "calendarEvent")

  return (
    <div data-test="calendar-container">
      <div style={{ height: 800 }}>
        <div id={id} className="dhx_cal_container" style={{ width: "100%", height: "100%" }}>
          <div className="dhx_cal_navline">
            <div className="dhx_cal_prev_button">&nbsp;</div>
            <div className="dhx_cal_next_button">&nbsp;</div>
            <div className="dhx_cal_today_button"></div>
            <div className="dhx_cal_date"></div>
            <div className="dhx_minical_icon" id="dhx_minical_icon" onClick={() => showMiniCalendar()}>
              &nbsp;
            </div>
            {/* @ts-ignore */}
            <div className="dhx_cal_tab" name="day_tab"></div>
            {/* @ts-ignore */}
            <div className="dhx_cal_tab" name="week_tab"></div>
            {/* @ts-ignore */}
            <div className="dhx_cal_tab" name="month_tab"></div>
          </div>
          <div className="dhx_cal_header"></div>
          <div className="dhx_cal_data"></div>
        </div>
      </div>

      <div ref={lightBoxRootElRef} className={classes.lightBoxRootElement}>
        {!currentEventId && (
          <CalendarForm
            canAddCalendarEvents={canAddCalendarEvents}
            canAddTasks={canAddTasks}
            canAddTodos={canAddTodos}
            onAddCalendarEvent={onCreateCalendarEvent}
            onAddAsTask={onCreateTask}
            onAddAsTodo={onCreateTodo}
            startDate={startDate}
            endDate={endDate}
            closeForm={closeForm}
            titleInputRef={eventFormTitleInputRef}
          />
        )}
        {shouldOpenCurrentEventDialog && (
          <Dialog
            open
            fullWidth
            maxWidth="md"
            PaperProps={{
              style: { padding: 12, minHeight: 600 },
            }}
            onClose={() => closeForm(false)}
          >
            <div className={classes.links}>
              {currentEvent.type === "task" && (
                <FullPath
                  projectLink={(entitiesById[currentEventId] as TaskViewModel)?.projectLink}
                  taskLink={(entitiesById[currentEventId] as TaskViewModel)?.taskLink}
                  ancestorTaskLinks={(entitiesById[currentEventId] as TaskViewModel)?.ancestorTaskLinks}
                  projectOrTaskNumber={(entitiesById[currentEventId] as TaskViewModel)?.shownTaskNumber}
                  onRedirect={() => closeForm(false)}
                />
              )}
              {(currentEvent.type === "todo" || currentEvent.type === "calendarEvent") && (
                <FullPath
                  projectLink={(entitiesById[currentEventId] as TodoViewModel | CalendarEventViewModel)?.projectLink}
                  taskLink={(entitiesById[currentEventId] as TodoViewModel | CalendarEventViewModel)?.taskLink}
                  ancestorTaskLinks={
                    (entitiesById[currentEventId] as TodoViewModel | CalendarEventViewModel)?.ancestorTaskLinks
                  }
                  projectOrTaskNumber={
                    (entitiesById[currentEventId] as TodoViewModel | CalendarEventViewModel)?.task?.taskNumber ||
                    (entitiesById[currentEventId] as TodoViewModel | CalendarEventViewModel)?.project?.projectNumber
                  }
                  showTaskLink={true}
                  onRedirect={() => closeForm(false)}
                />
              )}
            </div>
            <div className={classes.cardHeader}>
              <span className={classes.cardTitle}>
                {currentEvent.type === "task" && <TaskCardTitle task={entitiesById[currentEventId] as TaskViewModel} />}
                {currentEvent.type === "todo" && <TodoCardTitle todo={entitiesById[currentEventId] as TodoViewModel} />}
                {currentEvent.type === "calendarEvent" && (
                  <CalendarEventCardTitle calendarEvent={entitiesById[currentEventId] as CalendarEventViewModel} />
                )}
              </span>
              <IconButton className={classes.closeIcon} onClick={() => closeForm(false)}>
                <X size={24} color={"#616161"} />
              </IconButton>
            </div>
            <div style={{ padding: "16px" }}>
              {currentEvent.type === "task" && <TaskDetails taskId={currentEventId} />}
              {currentEvent.type === "todo" && <TodoDetails todo={entitiesById[currentEventId] as TodoViewModel} />}
              {currentEvent.type === "calendarEvent" && (
                <CalendarEventDetails
                  calendarEvent={entitiesById[currentEventId] as CalendarEventViewModel}
                  onDelete={() => {
                    closeForm(false)
                    clearState()
                    setCurrentEventId(null)
                  }}
                />
              )}
            </div>
          </Dialog>
        )}
        {shouldOpenNewEventDialog && (
          <Dialog
            open
            fullWidth
            maxWidth="md"
            PaperProps={{
              style: { padding: 12, minHeight: 600 },
            }}
            onClose={() => closeForm(false)}
          >
            <div className={classes.links}>
              {newEvent.type === "task" && (
                <FullPath
                  projectLink={(entitiesById[newlyCreatedEventId] as TaskViewModel)?.projectLink}
                  taskLink={(entitiesById[newlyCreatedEventId] as TaskViewModel)?.taskLink}
                  ancestorTaskLinks={(entitiesById[newlyCreatedEventId] as TaskViewModel)?.ancestorTaskLinks}
                  projectOrTaskNumber={(entitiesById[newlyCreatedEventId] as TaskViewModel)?.shownTaskNumber}
                  onRedirect={() => closeForm(false)}
                />
              )}
              {(newEvent.type === "todo" || newEvent.type === "calendarEvent") && (
                <FullPath
                  projectLink={
                    (entitiesById[newlyCreatedEventId] as TodoViewModel | CalendarEventViewModel)?.projectLink
                  }
                  taskLink={(entitiesById[newlyCreatedEventId] as TodoViewModel | CalendarEventViewModel)?.taskLink}
                  ancestorTaskLinks={
                    (entitiesById[newlyCreatedEventId] as TodoViewModel | CalendarEventViewModel)?.ancestorTaskLinks
                  }
                  projectOrTaskNumber={
                    (entitiesById[newlyCreatedEventId] as TodoViewModel | CalendarEventViewModel)?.task?.taskNumber ||
                    (entitiesById[newlyCreatedEventId] as TodoViewModel | CalendarEventViewModel)?.project
                      ?.projectNumber
                  }
                  showTaskLink={true}
                  onRedirect={() => closeForm(false)}
                />
              )}
            </div>
            <div className={classes.cardHeader}>
              <span className={classes.cardTitle}>
                {newEvent.type === "task" && (
                  <TaskCardTitle task={entitiesById[newlyCreatedEventId] as TaskViewModel} />
                )}
                {newEvent.type === "todo" && (
                  <TodoCardTitle todo={entitiesById[newlyCreatedEventId] as TodoViewModel} />
                )}
                {newEvent.type === "calendarEvent" && (
                  <CalendarEventCardTitle calendarEvent={entitiesById[newlyCreatedEventId] as CalendarEventViewModel} />
                )}
              </span>
              <IconButton className={classes.closeIcon} onClick={() => closeForm(false)}>
                <X size={24} color={"#616161"} />
              </IconButton>
            </div>
            <div style={{ padding: "16px" }}>
              {newEvent.type === "task" && <TaskDetails taskId={newlyCreatedEventId} />}
              {newEvent.type === "todo" && <TodoDetails todo={entitiesById[newlyCreatedEventId] as TodoViewModel} />}
              {newEvent.type === "calendarEvent" && (
                <CalendarEventDetails
                  calendarEvent={entitiesById[newlyCreatedEventId] as CalendarEventViewModel}
                  onDelete={() => {
                    closeForm(false)
                    clearState()
                    setCurrentEventId(null)
                  }}
                />
              )}
            </div>
          </Dialog>
        )}
        {openEditRecurringEventConfirmation && (
          <Dialog
            open
            fullWidth
            maxWidth="sm"
            PaperProps={{
              style: { padding: 12 },
            }}
          >
            <DialogContent style={{ textAlign: "center" }}>
              <Typography>{translations.editRecurringEventsDialogTitle}</Typography>
            </DialogContent>
            <DialogActions style={{ justifyContent: "center" }}>
              <Button
                variant="outlined"
                onClick={() => {
                  if (currentRecurringEvent) setCurrentEventId(currentRecurringEvent.event_pid)
                  clearState()
                }}
              >
                {translations.editSeries}
              </Button>
              <Button variant="outlined" onClick={handleClickEditOccurrence}>
                {translations.editOccurrence}
              </Button>
              <Button variant="outlined" onClick={() => clearState()}>
                {translations.cancel}
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </div>
    </div>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  lightBoxRootElement: {
    position: "absolute",
    top: "50% !important",
    left: "calc(50% + 24px) !important",
    transform: "translate(-50%, -50%)",
    zIndex: 10001,
    display: "none", // important to hide the light box initially
    background: theme.palette.common.white,
    transition: theme.transitions.create("height"),
    borderRadius: theme.spacing(0.5),
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-bewteen",
  },
  cardTitle: {
    flex: 1,
    padding: theme.spacing(1, 1.5),
  },
  closeIcon: {
    marginRight: 16,
  },
  links: {
    paddingLeft: theme.spacing(3),
  },
}))

type CalendarProps = {
  id?: string
  events: EventType[]
  recurringEvents: RecurringEventType[]
  entitiesById: Record<string, TaskViewModel | CalendarEventViewModel | TodoViewModel>
  canAddCalendarEvents: boolean
  canAddTasks: boolean
  canAddTodos: boolean
  onAddCalendarEvent: (calendarEvent: CalendarEventData) => Promise<void | CalendarEventViewModel>
  onAddTask: (task: CalendarTaskData) => Promise<void | TaskViewModel>
  onAddTodo: (task: CalendarTodoData) => Promise<void | TodoViewModel>
  onUpdateCalendarEvent: (eventId: string, updates: CalendarEventUpdates) => void
  onUpdateTask: (taskId: string, updates: CalendarTaskUpdates) => void
  onUpdateTodo: (todoId: string, updates: CalendarTodoUpdates) => void
  calendarSettings: CalendarSettingsViewModel
  showCaption?: boolean
}

const useTranslations = () => {
  const { t } = useTranslation("translation")
  const { t: tCalendarEvent } = useTranslation("calendarEvent")
  const dateTranslations = useDateTranslations()

  const today = t("today", { defaultValue: "Today" })
  const cancel = t("cancel", { defaultValue: "Cancel" })

  return {
    today,
    cancel,
    dayLabel: dateTranslations.dayLabel,
    weekLabel: dateTranslations.weekLabel,
    monthLabel: dateTranslations.monthLabel,
    fullDayNames: dateTranslations.fullDayNames,
    abbreviatedDayNames: dateTranslations.abbreviatedDayNames,
    fullMonthNames: dateTranslations.fullMonthNames,
    shortMonthsNames: dateTranslations.shortMonthsNames,
    editRecurringEventsDialogTitle: tCalendarEvent("editRecurringEventsDialogTitle", {
      defaultValue: "Do you want to edit the whole set of repeated events?",
    }),
    editSeries: tCalendarEvent("editSeries", { defaultValue: "Edit series" }),
    editOccurrence: tCalendarEvent("editOccurrence", { defaultValue: "Edit occurrence" }),
  }
}

const makeCalendarTranslations = (translations: ReturnType<typeof useTranslations>) => {
  const {
    today,
    dayLabel,
    weekLabel,
    monthLabel,
    fullDayNames,
    abbreviatedDayNames,
    fullMonthNames,
    shortMonthsNames,
  } = translations
  return {
    date: {
      month_full: fullMonthNames,
      month_short: shortMonthsNames,
      day_full: fullDayNames,
      day_short: abbreviatedDayNames,
    },
    labels: {
      dhx_cal_today_button: today,
      day_tab: dayLabel,
      week_tab: weekLabel,
      month_tab: monthLabel,
      day: dayLabel,
      week: weekLabel,
      month: monthLabel,
    },
  }
}

export default Calendar
